import React, { useState, useEffect } from 'react';
import { TrendingUp, PawPrint, Calendar } from 'lucide-react';
import { getTrends } from '../../services/api';

const MONTHS_SHORT = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const TYPE_LABELS = { vaccine: 'Aşı', treatment: 'Tedavi', service: 'Hizmet', product: 'Ürün' };
const TYPE_COLORS = { vaccine: '#34C759', treatment: '#6b6de3', service: '#FF9500', product: '#AF52DE' };
const formatCurrency = (a) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(a);

export default function TrendReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getTrends().then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-8 flex justify-center"><div className="w-5 h-5 border-2 border-[#6b6de3] border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return null;

  const revenueMax = Math.max(...(data.monthly_revenue?.map(m => m.total) || [1]), 1);
  const patientMax = Math.max(...(data.monthly_patients?.map(m => m.count) || [1]), 1);

  return (
    <div className="space-y-3">
      {/* Monthly Revenue Trend */}
      {data.monthly_revenue?.length > 0 && (
        <div className="ios-card">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={14} className="text-[#34C759]" />
            <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider">Ciro Artışı</p>
          </div>
          <div className="flex items-end gap-1" style={{ height: '100px' }}>
            {data.monthly_revenue.map((m, i) => {
              const h = Math.max((m.total / revenueMax) * 100, 4);
              const monthNum = parseInt(m.month.split('-')[1], 10);
              return (
                <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                  {m.total > 0 && <span className="text-[7px] text-[#8E8E93] mb-0.5">{formatCurrency(m.total)}</span>}
                  <div className="w-full max-w-[28px] bg-[#34C759] rounded-t transition-all" style={{ height: `${h}%` }} />
                  <span className="text-[8px] text-[#8E8E93] mt-1">{MONTHS_SHORT[monthNum - 1]}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Patient Growth Trend */}
      {data.monthly_patients?.length > 0 && (
        <div className="ios-card">
          <div className="flex items-center gap-2 mb-3">
            <PawPrint size={14} className="text-[#FF9500]" />
            <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider">Müşteri Artışı</p>
          </div>
          <div className="flex items-end gap-1" style={{ height: '80px' }}>
            {data.monthly_patients.map((m, i) => {
              const h = Math.max((m.count / patientMax) * 100, 4);
              const monthNum = parseInt(m.month.split('-')[1], 10);
              return (
                <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                  {m.count > 0 && <span className="text-[8px] text-[#8E8E93] mb-0.5">{m.count}</span>}
                  <div className="w-full max-w-[24px] bg-[#FF9500] rounded-t transition-all" style={{ height: `${h}%` }} />
                  <span className="text-[8px] text-[#8E8E93] mt-1">{MONTHS_SHORT[monthNum - 1]}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Seasonal Analysis */}
      {data.seasonal?.length > 0 && (
        <div className="ios-card">
          <div className="flex items-center gap-2 mb-3">
            <Calendar size={14} className="text-[#007AFF]" />
            <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider">Mevsimsel Analiz</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[10px]">
              <thead>
                <tr className="text-[#8E8E93]">
                  <th className="text-left py-1 px-1 font-medium">Ay</th>
                  {Object.keys(TYPE_LABELS).map(t => (
                    <th key={t} className="text-center py-1 px-1 font-medium">{TYPE_LABELS[t]}</th>
                  ))}
                  <th className="text-center py-1 px-1 font-medium">Toplam</th>
                </tr>
              </thead>
              <tbody>
                {data.seasonal.map((row) => {
                  const total = Object.values(row.data).reduce((s, v) => s + v, 0);
                  return (
                    <tr key={row.month} className="border-t border-gray-50">
                      <td className="py-1.5 px-1 font-medium text-gray-900">{MONTHS_SHORT[row.month - 1]}</td>
                      {Object.keys(TYPE_LABELS).map(t => (
                        <td key={t} className="text-center py-1.5 px-1">
                          <span style={{ color: TYPE_COLORS[t] }} className="font-semibold">{row.data[t] || '-'}</span>
                        </td>
                      ))}
                      <td className="text-center py-1.5 px-1 font-bold text-gray-900">{total}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
